// penagihan.repo — jejak "kapan terakhir dihubungi" per tiket.
// Tiketnya sendiri TIDAK diambil ulang di sini: daftar piutang memakai `listPayable`
// (payments.repo, sudah finScope + kasus klaim garansi) dan daftar unit-belum-diambil memakai
// `fetchPerluTickets`. Reuse sumber yang sudah benar, jangan bikin kriteria tandingan.
import { supabase } from '@core/supabase';
import { fetchAllPaged } from '@core/paging';
import { openWA } from '@core/wa';
import { currentUser } from '@core/session';

/** Status log khusus kiriman pengingat — dipisah dari log status servis agar mudah dicari. */
export const LOG_STATUS = 'Pengingat WA';

/** Rentang riwayat yang dibaca (hari). Cukup panjang agar kolom "terakhir dihubungi" berguna,
 *  cukup pendek agar hasilnya tak pernah menabrak batas baris PostgREST. */
const LOOKBACK_MIN = 30;

/**
 * Waktu (ms) kiriman pengingat TERAKHIR per nomor tiket, dalam rentang `jedaHari` terakhir
 * (minimal 30 hari). Sengaja TIDAK memakai daftar nomor tiket: pada ratusan tiket, daftar itu
 * membuat URL query kepanjangan dan seluruh layar gagal dimuat.
 */
export async function lastReminders(jedaHari: number): Promise<Map<string, number>> {
  const hari = Math.max(jedaHari, LOOKBACK_MIN);
  const sejak = new Date(Date.now() - hari * 86400_000).toISOString();
  const data = await fetchAllPaged<{ ticket_no: string; at: string }>((a, b) => supabase().from('ticket_logs')
    .select('ticket_no,at').eq('status', LOG_STATUS).gte('at', sejak)
    .order('at', { ascending: false }).order('id').range(a, b));  // menurun → yang PERTAMA ditemukan = paling baru
  const out = new Map<string, number>();
  for (const r of data) {
    if (out.has(r.ticket_no)) continue;
    const ms = Date.parse(r.at);
    if (Number.isFinite(ms)) out.set(r.ticket_no, ms);
  }
  return out;
}

/** Catat satu kiriman. Jejak ini yang membuat jeda "jangan tagih dua kali" bisa dipercaya. */
export async function logReminder(no: string, jenis: string, by: string): Promise<void> {
  const { error } = await supabase().from('ticket_logs')
    .insert({ ticket_no: no, status: LOG_STATUS, catatan: jenis, by_user: by });
  if (error) throw new Error(error.message);
}

/**
 * SATU pintu untuk semua tombol 💬 pengingat/penagihan ke pelanggan (menu Penagihan, Status
 * Hutang/Piutang, Laporan Piutang/DP, Perlu Diperhatikan).
 *
 * Kenapa harus lewat sini: kalau ada tombol yang mengirim TANPA mencatat, kolom "terakhir
 * dihubungi" dan jeda anti-tagih-ganda menjadi bohong — pelanggan bisa ditagih dua kali dalam
 * lima menit dan tak ada layar yang memperlihatkannya.
 *
 * Pencatatan best-effort: WA sudah telanjur terbuka, jadi kegagalan mencatat tak boleh
 * membuat aksinya terasa gagal — tapi `onGagal` diberi kesempatan memberi tahu petugas.
 */
export function kirimPengingat(no: string, wa: string | null | undefined, teks: string, jenis: string, onGagal?: (m: string) => void): void {
  openWA(wa, teks);
  void logReminder(no, jenis, currentUser()?.nama ?? '')
    .catch((e: Error) => onGagal?.('WA dibuka, tapi catatannya gagal disimpan: ' + e.message));
}
